import React, { useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@material-ui/icons/Close';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getAccountBlockInfo } from '../../types/company';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 10px 36px rgba(15,23,42,0.16)',
    overflow: 'hidden',
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
  },
  dialogHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  headerIconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#fff7ed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIcon: {
    fontSize: 22,
    color: '#ea580c',
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  closeButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: '#f1f5f9',
      color: '#334155',
    },
  },
  content: {
    padding: '24px 24px 16px',
    backgroundColor: '#f8fafc',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px 12px',
    },
  },
  contentIconWrapper: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: '#fff7ed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  contentIcon: {
    fontSize: 34,
    color: '#f97316',
  },
  message: {
    textAlign: 'center',
    color: '#475569',
    lineHeight: 1.65,
    marginBottom: theme.spacing(1),
  },
  helperMessage: {
    textAlign: 'center',
    color: '#0f172a',
    fontWeight: 600,
    marginTop: theme.spacing(1.5),
  },
  actions: {
    justifyContent: 'center',
    padding: '8px 24px 24px',
    gap: 10,
    backgroundColor: '#f8fafc',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      padding: '8px 16px 16px',
    },
  },
  button: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
    minWidth: 122,
    height: 40,
  },
}));

const BlockedModal = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const companyStatus = user?.company?.status;
  const { reason, title, message } = getAccountBlockInfo(companyStatus, user?.trial);
  const isTrialExpired = reason === 'TRIAL_EXPIRED';

  const handleResolveBlock = () => {
    onClose();
    history.push(isTrialExpired ? '/subscription/plans' : '/subscription/invoice');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderLeft}>
          <Box className={classes.headerIconWrapper}>
            <WarningAmberIcon className={classes.headerIcon} />
          </Box>
          <Box minWidth={0}>
            <Typography className={classes.headerTitle} component="h2">
              {title}
            </Typography>
            <Typography className={classes.headerSubtitle} component="p">
              {isTrialExpired
                ? 'Seu período de teste terminou e é necessário contratar um plano.'
                : 'Identificamos uma fatura vencida e pendente de pagamento.'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={onClose}
          aria-label="Fechar modal"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent className={classes.content}>
        <Box className={classes.contentIconWrapper}>
          <WarningAmberIcon className={classes.contentIcon} />
        </Box>
        <Typography className={classes.message}>{message}</Typography>
        <Typography className={classes.helperMessage}>
          {isTrialExpired
            ? 'Para continuar usando o sistema, escolha um plano ativo.'
            : 'Para desbloquear o acesso, regularize a fatura vencida na tela de faturas.'}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="outlined" onClick={onClose} className={classes.button}>
          Fechar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleResolveBlock}
          className={classes.button}
        >
          {isTrialExpired ? 'Ver planos' : 'Ver faturas'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlockedModal;
